import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-hot-toast';
import { Aufgabe, AufgabeKommentar, AufgabeStatus, CreateAufgabeRequest, UpdateAufgabeRequest, CreateAufgabeStatusRequest, User } from '../types';
import aufgabeService from '../services/aufgabeService';
import { useAuth } from '../contexts/AuthContext';
import { Icon, PageHeader, LoadingScreen, EmptyState, FilterStand, FilterZuruecksetzen, AnlegenButton, filterAktiv, fehlerText } from '../components/ui';
import { useStueckweise } from '../utils/stueckweise';
import { ankunft, markiereNeu } from '../utils/ankunft';
import { DatumFeld } from '../components/DatumZeit';
import { useSearchParams } from 'react-router-dom';

// ---- helpers ----

const formatDate = (iso?: string) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return iso; }
};

const formatDateTime = (iso?: string) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('de-DE', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

const formatRelative = (iso?: string) => {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'gerade eben';
  if (min < 60) return `vor ${min} Min.`;
  const h = Math.floor(min / 60);
  if (h < 24) return `vor ${h} Std.`;
  const d = Math.floor(h / 24);
  if (d === 1) return 'gestern';
  if (d < 7) return `vor ${d} Tagen`;
  return formatDate(iso);
};

const isDue = (faelligAm?: string) => {
  if (!faelligAm) return false;
  return new Date(faelligAm) < new Date();
};

// ---- Status Badge ----

const StatusBadge: React.FC<{ name: string; farbe: string }> = ({ name, farbe }) => (
  <span
    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium text-white"
    style={{ backgroundColor: farbe }}
  >
    {name}
  </span>
);

// ---- Detail Modal ----

interface DetailModalProps {
  aufgabe: Aufgabe;
  statusList: AufgabeStatus[];
  currentUserId?: number;
  onClose: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onKommentarAdded: (kommentar: AufgabeKommentar) => void;
  onKommentarDeleted: (id: number) => void;
}

const DetailModal: React.FC<DetailModalProps> = ({ aufgabe, currentUserId, onClose, onEdit, onDelete, onKommentarAdded, onKommentarDeleted }) => {
  const [kommentarText, setKommentarText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleKommentarSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!kommentarText.trim()) return;
    setSubmitting(true);
    try {
      const k = await aufgabeService.addKommentar(aufgabe.id, kommentarText.trim());
      onKommentarAdded(k);
      setKommentarText('');
    } catch (err) {
      toast.error(fehlerText(err, 'Kommentar konnte nicht gespeichert werden'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleKommentarDelete = async (id: number) => {
    try {
      await aufgabeService.deleteKommentar(id);
      onKommentarDeleted(id);
    } catch (err) {
      toast.error(fehlerText(err, 'Kommentar konnte nicht gelöscht werden'));
    }
  };

  return (
  <div className="ui-fade fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
    <div className="ui-pop bg-white rounded-lg w-full max-w-lg max-h-[90vh] flex flex-col">
      {/* Header */}
      <div className="flex items-start justify-between p-5 border-b">
        <div className="flex-1 min-w-0">
          <h2 className="text-lg font-semibold text-gray-900 break-words">{aufgabe.thema}</h2>
          <div className="mt-1 flex items-center gap-2 flex-wrap">
            <StatusBadge name={aufgabe.statusName} farbe={aufgabe.statusFarbe} />
            {aufgabe.faelligAm && (
              <span className={`text-xs ${isDue(aufgabe.faelligAm) && !aufgabe.istAbschluss ? 'text-red-600 font-semibold' : 'text-gray-500'}`}>
                Fällig: {formatDate(aufgabe.faelligAm)}
                {isDue(aufgabe.faelligAm) && !aufgabe.istAbschluss && ' (überfällig)'}
              </span>
            )}
          </div>
        </div>
        <button onClick={onClose} aria-label="Schließen" className="ml-3 text-gray-400 hover:text-gray-600 flex-shrink-0">
          <Icon.Close className="w-6 h-6" />
        </button>
      </div>

      {/* Scrollable body */}
      <div className="overflow-y-auto flex-1 p-5 space-y-4">
        {/* Meta */}
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <p className="text-xs text-gray-400">Erstellt von</p>
            <p className="text-gray-700">{aufgabe.erstelltVonName}</p>
          </div>
          <div>
            <p className="text-xs text-gray-400">Erstellungsdatum</p>
            <p className="text-gray-700">{formatDateTime(aufgabe.erstelltAm)}</p>
          </div>
          <div>
            <p className="text-xs text-gray-400">Zugewiesen an</p>
            <p className="text-gray-700">{aufgabe.zugewiesenAnName ?? ''}</p>
          </div>
          {aufgabe.zuletztBearbeitetVonName && (
            <>
              <div>
                <p className="text-xs text-gray-400">Zuletzt bearbeitet von</p>
                <p className="text-gray-700">{aufgabe.zuletztBearbeitetVonName}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400">Zuletzt bearbeitet</p>
                <p className="text-gray-700">{formatDateTime(aufgabe.zuletztBearbeitetAm)}</p>
              </div>
            </>
          )}
        </div>

        {/* Beschreibung */}
        {aufgabe.beschreibung && (
          <div>
            <p className="text-xs text-gray-400 mb-1">Beschreibung</p>
            <p className="text-sm text-gray-700 whitespace-pre-wrap bg-gray-50 rounded p-3">{aufgabe.beschreibung}</p>
          </div>
        )}

        {/* Kommentare */}
        <div>
          <p className="text-xs text-gray-400 mb-2">Kommentare ({aufgabe.kommentare.length})</p>
          <div className="space-y-2 mb-3">
            {aufgabe.kommentare.length === 0 && (
              <p className="text-sm text-gray-400 italic">Noch keine Kommentare</p>
            )}
            {aufgabe.kommentare.map((k) => (
              <div key={k.id} className="bg-gray-50 rounded-lg p-3 group relative">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium text-gray-700">{k.benutzerName}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-400">{formatDateTime(k.erstelltAm)}</span>
                    {(currentUserId === k.benutzerId) && (
                      <button
                        onClick={() => handleKommentarDelete(k.id)}
                        className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 transition-all"
                        title="Kommentar löschen"
                      >
                        <Icon.Close className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{k.text}</p>
              </div>
            ))}
          </div>
          <form onSubmit={handleKommentarSubmit} className="flex gap-2">
            <textarea
              ref={textareaRef}
              rows={2}
              value={kommentarText}
              onChange={(e) => setKommentarText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handleKommentarSubmit(e as any);
              }}
              placeholder="Kommentar schreiben… (Strg+Enter zum Senden)"
              className="flex-1 p-2 border border-gray-300 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
            <button
              type="submit"
              disabled={submitting || !kommentarText.trim()}
              className="px-3 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-40 self-end text-sm"
              aria-label="Kommentar senden"
              title="Kommentar senden"
            >
              <Icon.ArrowRight className="w-4 h-4" />
            </button>
          </form>
        </div>

        {/* Verlauf */}
        <div>
          <p className="text-xs text-gray-400 mb-2">Bearbeitungsverlauf</p>
          {aufgabe.verlauf.length === 0 ? (
            <p className="text-sm text-gray-400 italic">Kein Verlauf vorhanden</p>
          ) : (
            <div className="relative border-l-2 border-gray-200 ml-2 space-y-3">
              {aufgabe.verlauf.map((eintrag) => (
                <div key={eintrag.id} className="ml-4 relative">
                  <div className="absolute -left-6 top-1 w-3 h-3 rounded-full bg-brand-600 border-2 border-white"></div>
                  <p className="text-xs text-gray-400">{formatDateTime(eintrag.zeitpunkt)} · {eintrag.benutzerName}</p>
                  <p className="text-sm text-gray-700 mt-0.5">{eintrag.aktion}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="p-4 border-t flex justify-between">
        <button
          onClick={onDelete}
          className="px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
        >
          Löschen
        </button>
        <div className="flex gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg">
            Schließen
          </button>
          <button onClick={onEdit} className="px-4 py-2 text-sm bg-brand-600 text-white hover:bg-brand-700 rounded-lg">
            Bearbeiten
          </button>
        </div>
      </div>
    </div>
  </div>
  );
};

// ---- Form Modal ----

interface FormModalProps {
  statusList: AufgabeStatus[];
  benutzerList: User[];
  editingAufgabe: Aufgabe | null;
  onClose: () => void;
  onSaved: (id: number) => void;
}

const FormModal: React.FC<FormModalProps> = ({ statusList, benutzerList, editingAufgabe, onClose, onSaved }) => {
  const [formData, setFormData] = useState<CreateAufgabeRequest>({
    thema: editingAufgabe?.thema ?? '',
    beschreibung: editingAufgabe?.beschreibung ?? '',
    faelligAm: editingAufgabe?.faelligAm ?? '',
    statusId: editingAufgabe?.statusId ?? (statusList[0]?.id ?? 0),
    zugewiesenAnId: editingAufgabe?.zugewiesenAnId ?? undefined,
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...formData,
      faelligAm: formData.faelligAm || undefined,
      beschreibung: formData.beschreibung || undefined,
    };
    try {
      if (editingAufgabe) {
        const a = await aufgabeService.update(editingAufgabe.id, payload as UpdateAufgabeRequest);
        toast.success('Aufgabe gespeichert');
        onSaved(a?.id ?? editingAufgabe.id);
      } else {
        const a = await aufgabeService.create(payload);
        toast.success('Aufgabe angelegt');
        onSaved(a.id);
      }
    } catch (err) {
      toast.error(fehlerText(err, editingAufgabe ? 'Aufgabe konnte nicht gespeichert werden' : 'Aufgabe konnte nicht angelegt werden'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ui-fade fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="ui-pop bg-white rounded-lg w-full max-w-md">
        <div className="flex items-center justify-between p-5 border-b">
          <h2 className="text-lg font-semibold">{editingAufgabe ? 'Aufgabe bearbeiten' : 'Aufgabe anlegen'}</h2>
          <button onClick={onClose} aria-label="Schließen" className="text-gray-400 hover:text-gray-600">
            <Icon.Close className="w-6 h-6" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Thema *</label>
            <input
              type="text"
              required
              value={formData.thema}
              onChange={(e) => setFormData({ ...formData, thema: e.target.value })}
              className="w-full p-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              placeholder="Kurze Beschreibung der Aufgabe"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Beschreibung</label>
            <textarea
              rows={3}
              value={formData.beschreibung}
              onChange={(e) => setFormData({ ...formData, beschreibung: e.target.value })}
              className="w-full p-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 resize-none"
              placeholder="Optionale Details..."
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fälligkeitsdatum</label>
              <DatumFeld
                value={formData.faelligAm ?? ''}
                onChange={(v) => setFormData({ ...formData, faelligAm: v })}
                className="w-full p-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status *</label>
              <select
                required
                value={formData.statusId}
                onChange={(e) => setFormData({ ...formData, statusId: Number(e.target.value) })}
                className="w-full p-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              >
                {statusList.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Zugewiesen an</label>
            <select
              value={formData.zugewiesenAnId ?? ''}
              onChange={(e) => setFormData({ ...formData, zugewiesenAnId: e.target.value ? Number(e.target.value) : undefined })}
              className="w-full p-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            >
              <option value="">Niemand</option>
              {benutzerList.map((u) => (
                <option key={u.id} value={u.id}>{u.firstName} {u.lastName}</option>
              ))}
            </select>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg">
              Abbrechen
            </button>
            <button type="submit" disabled={saving} className="px-4 py-2 text-sm bg-brand-600 text-white hover:bg-brand-700 rounded-lg disabled:opacity-50">
              {saving ? 'Wird gespeichert…' : (editingAufgabe ? 'Speichern' : 'Aufgabe anlegen')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ---- Status Management Modal ----

interface StatusModalProps {
  statusList: AufgabeStatus[];
  onClose: () => void;
  onChanged: () => void;
}

const DEFAULT_COLORS = ['#EF4444', '#F59E0B', '#10B981', '#3B82F6', '#8B5CF6', '#EC4899', '#6B7280', '#163056'];

const StatusModal: React.FC<StatusModalProps> = ({ statusList, onClose, onChanged }) => {
  const [newName, setNewName] = useState('');
  const [newFarbe, setNewFarbe] = useState('#6B7280');
  const [newIstAbschluss, setNewIstAbschluss] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setSaving(true);
    try {
      const req: CreateAufgabeStatusRequest = {
        name: newName.trim(),
        farbe: newFarbe,
        reihenfolge: statusList.length,
        istAbschluss: newIstAbschluss,
      };
      await aufgabeService.createStatus(req);
      toast.success('Status angelegt');
      setNewName('');
      setNewIstAbschluss(false);
      onChanged();
    } catch (err) {
      toast.error(fehlerText(err, 'Status konnte nicht angelegt werden'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (status: AufgabeStatus) => {
    if (!window.confirm(`Status „${status.name}“ löschen?`)) return;
    try {
      await aufgabeService.deleteStatus(status.id);
      toast.success('Status gelöscht');
      onChanged();
    } catch (err) {
      toast.error(fehlerText(err, 'Status konnte nicht gelöscht werden'));
    }
  };

  return (
    <div className="ui-fade fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="ui-pop bg-white rounded-lg w-full max-w-md">
        <div className="flex items-center justify-between p-5 border-b">
          <h2 className="text-lg font-semibold">Status verwalten</h2>
          <button onClick={onClose} aria-label="Schließen" className="text-gray-400 hover:text-gray-600">
            <Icon.Close className="w-6 h-6" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          {/* Existing statuses */}
          <div className="space-y-2">
            {statusList.map((status) => (
              <div key={status.id} className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg">
                <div className="w-4 h-4 rounded-full flex-shrink-0" style={{ backgroundColor: status.farbe }}></div>
                <span className="flex-1 text-sm text-gray-700">{status.name}</span>
                {status.istAbschluss && (
                  <span className="text-xs text-green-600 bg-green-100 px-2 py-0.5 rounded-full">Abschluss</span>
                )}
                <button
                  onClick={() => handleDelete(status)}
                  className="text-gray-400 hover:text-red-500 transition-colors"
                  title="Status löschen"
                >
                  <Icon.Trash className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>

          {/* New status form */}
          <form onSubmit={handleCreate} className="border-t pt-4 space-y-3">
            <p className="text-sm font-medium text-gray-700">Status anlegen</p>
            <div className="flex gap-2">
              <input
                type="text"
                required
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                className="flex-1 p-2 border border-gray-300 rounded-lg text-sm"
                placeholder="Statusname"
              />
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Farbe</p>
              <div className="flex gap-2 flex-wrap">
                {DEFAULT_COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setNewFarbe(c)}
                    className={`w-7 h-7 rounded-full border-2 ${newFarbe === c ? 'border-gray-800 scale-110' : 'border-transparent'} transition-transform`}
                    style={{ backgroundColor: c }}
                  />
                ))}
                <input
                  type="color"
                  value={newFarbe}
                  onChange={(e) => setNewFarbe(e.target.value)}
                  className="w-7 h-7 rounded cursor-pointer border border-gray-300"
                  title="Benutzerdefinierte Farbe"
                />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={newIstAbschluss}
                onChange={(e) => setNewIstAbschluss(e.target.checked)}
                className="rounded"
              />
              Als Abschlussstatus markieren (Aufgabe gilt als erledigt)
            </label>
            <button
              type="submit"
              disabled={saving || !newName.trim()}
              className="w-full py-2 bg-brand-600 text-white text-sm rounded-lg hover:bg-brand-700 disabled:opacity-50"
            >
              {saving ? 'Wird angelegt…' : 'Status anlegen'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

// ---- Main Page ----

const Aufgaben: React.FC = () => {
  const { isAdmin, user } = useAuth();
  const [aufgaben, setAufgaben] = useState<Aufgabe[]>([]);
  const [statusList, setStatusList] = useState<AufgabeStatus[]>([]);
  const [benutzerList, setBenutzerList] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortStatusId, setSortStatusId] = useState<number | null>(null);
  const [selectedAufgabe, setSelectedAufgabe] = useState<Aufgabe | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [showForm, setShowForm] = useState(false);
  const [editingAufgabe, setEditingAufgabe] = useState<Aufgabe | null>(null);
  const [showStatusModal, setShowStatusModal] = useState(false);

  const load = useCallback(async () => {
    try {
      const [aufgabenData, statusData, benutzerData] = await Promise.all([
        aufgabeService.getAll(),
        aufgabeService.getAllStatus(),
        aufgabeService.getBenutzer(),
      ]);
      setAufgaben(aufgabenData);
      setStatusList(statusData);
      setBenutzerList(benutzerData);
    } catch (err) {
      toast.error(fehlerText(err, 'Aufgaben konnten nicht geladen werden'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Aus der Suche (`/aufgaben?id=12`): die Aufgabe gleich öffnen.
  const sprungId = Number(searchParams.get('id')) || 0;
  useEffect(() => {
    if (!sprungId) return;
    setSearchParams({}, { replace: true });
    aufgabeService.getById(sprungId)
      .then(setSelectedAufgabe)
      .catch((err) => toast.error(fehlerText(err, 'Aufgabe konnte nicht geladen werden')));
  }, [sprungId, setSearchParams]);

  const handleOpenDetail = async (aufgabe: Aufgabe) => {
    try {
      const detail = await aufgabeService.getById(aufgabe.id);
      setSelectedAufgabe(detail);
    } catch (err) {
      toast.error(fehlerText(err, 'Aufgabe konnte nicht geladen werden'));
    }
  };

  const handleEdit = () => {
    if (!selectedAufgabe) return;
    setEditingAufgabe(selectedAufgabe);
    setSelectedAufgabe(null);
    setShowForm(true);
  };

  const handleDelete = async () => {
    if (!selectedAufgabe) return;
    if (!window.confirm(`Aufgabe „${selectedAufgabe.thema}“ löschen?`)) return;
    try {
      await aufgabeService.delete(selectedAufgabe.id);
      toast.success('Aufgabe gelöscht');
      setSelectedAufgabe(null);
      await load();
    } catch (err) {
      toast.error(fehlerText(err, 'Aufgabe konnte nicht gelöscht werden'));
    }
  };

  const handleFormSaved = async (id: number) => {
    markiereNeu(`aufgabe-${id}`);
    setShowForm(false);
    setEditingAufgabe(null);
    await load();
  };

  // Nur die Suche filtert; die Status-Chips sortieren bloß nach vorn und blenden nichts aus.
  const filterIstAktiv = searchTerm.trim() !== '';
  const filterZuruecksetzen = () => setSearchTerm('');
  const neueAufgabe = () => { setEditingAufgabe(null); setShowForm(true); };

  const filtered = React.useMemo(() => aufgaben
    .filter((a) => a.thema.toLowerCase().includes(searchTerm.toLowerCase()))
    .sort((a, b) => {
      if (sortStatusId === null) return 0;
      const aMatch = a.statusId === sortStatusId ? 0 : 1;
      const bMatch = b.statusId === sortStatusId ? 0 : 1;
      return aMatch - bMatch;
    }), [aufgaben, searchTerm, sortStatusId]);
  const { sichtbar, Ende } = useStueckweise(filtered);

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className="p-4 md:p-6">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-gray-100 pb-3 -mx-4 px-4 -mt-4 pt-4 md:-mx-6 md:px-6 md:-mt-6 md:pt-6">
        <PageHeader title="Aufgaben" className="!mb-3" actions={<>
            {isAdmin() && (
              <button
                onClick={() => setShowStatusModal(true)}
                className="text-gray-500 hover:text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors text-sm flex items-center gap-1"
                title="Status verwalten"
              >
                <Icon.Regler className="w-4 h-4" />
                <span className="hidden md:inline">Status</span>
              </button>
            )}
            <AnlegenButton onClick={neueAufgabe}>Aufgabe anlegen</AnlegenButton>
            
        </>} />

        {/* Search + Status sort chips */}
        <div className="flex flex-wrap items-center gap-2 mb-2">
          <div className="relative flex-1 min-w-[12rem]">
            <Icon.Search className={`pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 ${searchTerm ? 'text-brand-600' : 'text-gray-400'}`} />
            <input
              type="text"
              placeholder="Aufgaben suchen …"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-full p-2 pl-8 border border-gray-300 rounded-lg text-sm ${searchTerm ? filterAktiv : ''}`}
            />
          </div>
          <FilterStand
            treffer={filtered.length}
            gesamt={aufgaben.length}
            einheit="Aufgaben"
            aktiv={filterIstAktiv}
            onZuruecksetzen={filterZuruecksetzen}
          />
        </div>
        {statusList.length > 0 && (
          <div className="flex gap-1.5 flex-wrap">
            {statusList.map((s) => (
              <button
                key={s.id}
                onClick={() => setSortStatusId(sortStatusId === s.id ? null : s.id)}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-all border ${
                  sortStatusId === s.id
                    ? 'text-white border-transparent'
                    : 'text-gray-600 bg-white border-gray-200 hover:border-gray-400'
                }`}
                style={sortStatusId === s.id ? { backgroundColor: s.farbe, borderColor: s.farbe } : {}}
              >
                <span
                  className="w-2 h-2 rounded-full flex-shrink-0"
                  style={{ backgroundColor: sortStatusId === s.id ? 'rgba(255,255,255,0.7)' : s.farbe }}
                />
                {s.name}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* List */}
      <div className="space-y-2">
        {sichtbar.map((aufgabe) => {
          const overdue = isDue(aufgabe.faelligAm) && !aufgabe.istAbschluss;
          const a = ankunft(`aufgabe-${aufgabe.id}`);
          return (
            <button
              key={aufgabe.id}
              ref={a.ref}
              onClick={() => handleOpenDetail(aufgabe)}
              className={`w-full text-left bg-white rounded-lg shadow-sm border border-gray-100 hover:border-brand-300 hover:shadow-md transition-all ${a.className}`}
            >
              <div className="flex items-center p-3 md:p-4 gap-3">
                {/* Status dot */}
                <div
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: aufgabe.statusFarbe }}
                ></div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{aufgabe.thema}</p>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    {/* Ersteller + Datum */}
                    <span className="inline-flex items-center gap-1 text-xs text-gray-400">
                      <Icon.Person className="w-3 h-3" />
                      {aufgabe.erstelltVonName} · {formatDate(aufgabe.erstelltAm)}
                    </span>
                    {/* Zuletzt bearbeitet */}
                    {aufgabe.zuletztBearbeitetVonName && (
                      <span className="inline-flex items-center gap-1 text-xs text-gray-400">
                        <Icon.Edit className="w-3 h-3" />
                        {aufgabe.zuletztBearbeitetVonName} · {formatRelative(aufgabe.zuletztBearbeitetAm)}
                      </span>
                    )}
                    {/* Zuweisung */}
                    {aufgabe.zugewiesenAnName && (
                      <span className="inline-flex items-center gap-1 text-xs text-gray-500 font-medium">
                        <Icon.Aufgaben className="w-3 h-3" />
                        {aufgabe.zugewiesenAnName}
                      </span>
                    )}
                    {/* Fälligkeit */}
                    {aufgabe.faelligAm && (
                      <span className={`inline-flex items-center gap-1 text-xs font-medium ${overdue ? 'text-red-600' : 'text-gray-500'}`}>
                        <Icon.Kalender className="w-3 h-3" />
                        {overdue ? 'Überfällig · ' : ''}{formatDate(aufgabe.faelligAm)}
                      </span>
                    )}
                  </div>
                </div>

                {/* Right badges */}
                <div className="flex-shrink-0">
                  {/* Statuswechsel blendet nur beim gerade geänderten Eintrag weich ein. */}
                  {a.className
                    ? <span key={aufgabe.statusId} className="ui-wechsel inline-flex"><StatusBadge name={aufgabe.statusName} farbe={aufgabe.statusFarbe} /></span>
                    : <StatusBadge name={aufgabe.statusName} farbe={aufgabe.statusFarbe} />}
                </div>

                {/* Chevron */}
                <Icon.ChevronRight className="w-4 h-4 text-gray-300 flex-shrink-0" />
              </div>
            </button>
          );
        })}
        {Ende}
      </div>

      {filtered.length === 0 && (
        aufgaben.length > 0
          ? <EmptyState icon={<Icon.Search />} title="Keine Aufgaben gefunden" hint="Keine Aufgabe passt zur Suche." action={<FilterZuruecksetzen onClick={filterZuruecksetzen} />} />
          : <EmptyState icon={<Icon.Aufgaben />} title="Noch keine Aufgaben" hint="Legen Sie die erste Aufgabe an und weisen Sie sie jemandem zu." action={<AnlegenButton onClick={neueAufgabe}>Aufgabe anlegen</AnlegenButton>} />
      )}

      {/* Modals */}
      {selectedAufgabe && (
        <DetailModal
          aufgabe={selectedAufgabe}
          statusList={statusList}
          currentUserId={user?.id}
          onClose={() => setSelectedAufgabe(null)}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onKommentarAdded={(k) => setSelectedAufgabe((prev) => prev ? { ...prev, kommentare: [...prev.kommentare, k] } : prev)}
          onKommentarDeleted={(id) => setSelectedAufgabe((prev) => prev ? { ...prev, kommentare: prev.kommentare.filter((k) => k.id !== id) } : prev)}
        />
      )}
      {showForm && (
        <FormModal
          statusList={statusList}
          benutzerList={benutzerList}
          editingAufgabe={editingAufgabe}
          onClose={() => { setShowForm(false); setEditingAufgabe(null); }}
          onSaved={handleFormSaved}
        />
      )}
      {showStatusModal && (
        <StatusModal
          statusList={statusList}
          onClose={() => setShowStatusModal(false)}
          onChanged={async () => {
            const statusData = await aufgabeService.getAllStatus();
            setStatusList(statusData);
          }}
        />
      )}
    </div>
  );
};

export default Aufgaben;
